import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

const GlobalStyle = createGlobalStyle`
  /* CSS Reset and Base Styles */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    background: linear-gradient(135deg, ${theme.colors.neutral.gray50} 0%, ${theme.colors.neutral.gray100} 100%);
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.base};
    line-height: ${theme.typography.lineHeight.normal};
    color: ${theme.colors.neutral.gray800};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  html, body, #root {
    height: 100%;
    min-height: 100vh;
  }

  #root {
    -webkit-transform: translate3d(0, 0, 0);
    display: flex;
    flex-direction: column;
  }

  /* Modern Input Styles */
  input {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.base};
    border: 1px solid ${theme.colors.neutral.gray200};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    transition: all ${theme.transitions.normal};
    background: ${theme.colors.neutral.white};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary.main};
      box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.1);
    }
    
    &:disabled {
      background: ${theme.colors.neutral.gray100};
      color: ${theme.colors.neutral.gray400};
      cursor: not-allowed;
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield; /* Firefox */
  }

  /* Modern Button Base Styles */
  button {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.medium};
    border: none;
    border-radius: ${theme.borderRadius.lg};
    padding: ${theme.spacing[3]} ${theme.spacing[6]};
    cursor: pointer;
    transition: all ${theme.transitions.normal};
    background: ${theme.colors.primary.gradient};
    color: ${theme.colors.neutral.white};
    box-shadow: ${theme.shadows.base};
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.lg};
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: ${theme.shadows.sm};
    }
    
    &:disabled {
      background: ${theme.colors.neutral.gray300};
      color: ${theme.colors.neutral.gray500};
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  /* Typography Reset */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: ${theme.typography.fontWeight.semibold};
    line-height: ${theme.typography.lineHeight.tight};
    color: ${theme.colors.neutral.gray900};
  }

  h1 { font-size: ${theme.typography.fontSize['3xl']}; }
  h2 { font-size: ${theme.typography.fontSize['2xl']}; }
  h3 { font-size: ${theme.typography.fontSize.xl}; }
  h4 { font-size: ${theme.typography.fontSize.lg}; }
  h5 { font-size: ${theme.typography.fontSize.base}; }
  h6 { font-size: ${theme.typography.fontSize.sm}; }

  p {
    margin: 0;
    line-height: ${theme.typography.lineHeight.normal};
  }

  /* Modern Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.neutral.gray100};
    border-radius: ${theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral.gray300};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.neutral.gray400};
    }
  }

  /* Focus styles for accessibility */
  *:focus-visible {
    outline: 2px solid ${theme.colors.primary.main};
    outline-offset: 2px;
  }

  /* Selection styles */
  ::selection {
    background: ${theme.colors.primary.light};
    color: ${theme.colors.neutral.gray900};
  }
`;

export default GlobalStyle;
